import BaseApiService from '@/services/base-api.service';
import { API_BASE_URL } from '@/config/api';
import { Link, LinkResponse, LinkQueryParams, CreateLinkRequest, Tag } from '../types';
import useTagStore from '../stores/tag.store';

interface ApiResponse {
  items: Link[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

class LinkService extends BaseApiService {
  constructor() {
    super(API_BASE_URL);
  }

  async getLinks(params: LinkQueryParams = {}): Promise<LinkResponse> {
    const queryParams = new URLSearchParams();

    if (params.search) {
      queryParams.append('query', params.search);
    }

    // Handle limit parameter
    const limit = params.page_size || params.limit || 25;
    queryParams.append('limit', limit.toString());

    // Handle offset parameter
    let offset = 0;
    if (params.page && params.page_size) {
      offset = (params.page - 1) * params.page_size;
    } else if (params.skip !== undefined) {
      offset = params.skip;
    }
    queryParams.append('offset', offset.toString());

    // Handle sort direction
    if (params.sort_by) {
      queryParams.append('sort_by', params.sort_by);
    }
    const sortDesc = params.order === 'desc' || params.sort_desc === true;
    queryParams.append('sort_desc', sortDesc.toString());

    // Handle collection and tag filters
    if (typeof params.collection_id === 'number') {
      queryParams.append('collection_id', params.collection_id.toString());
    }

    if (params.tag_id) {
      queryParams.append('tag_id', params.tag_id.toString());
    }

    if (params.is_favorite !== undefined) {
      queryParams.append('is_favorite', params.is_favorite.toString());
    }

    if (params.is_archived !== undefined) {
      queryParams.append('is_archived', params.is_archived.toString());
    }

    const endpoint = params.search ? '/search/link' : '/link/';
    const response = await this.get<LinkResponse>(endpoint, { params: queryParams });
    
    // Ensure tags are properly initialized if they don't exist
    if (response.data && Array.isArray(response.data.items)) {
      response.data.items = response.data.items.map(item => ({
        ...item,
        tags: item.tags || [],
        tag_ids: item.tag_ids || []
      }));
    }
    
    return response.data;
  }

  async createLink(data: Partial<Link>): Promise<Link> {
    const response = await this.post<Link>('/link/', data);
    return response.data;
  }

  async updateLink(id: number, data: Partial<Link>): Promise<Link> {
    const response = await this.put<Link>(`/link/${id}`, {
      ...data,
      // Ensure tag_ids is always an array
      tag_ids: data.tag_ids || []
    });
    
    // Ensure tags are properly initialized in the response
    return {
      ...response.data,
      tags: response.data.tags || [],
      tag_ids: response.data.tag_ids || []
    };
  }

  async deleteLink(id: number): Promise<void> {
    await this.delete(`/link/${id}`);
  }

  async toggleFavorite(id: number): Promise<Link> {
    const response = await this.post<Link>(`/link/${id}/favorite`);
    return response.data;
  }

  async toggleArchive(id: number): Promise<Link> {
    const response = await this.post<Link>(`/link/${id}/archive`);
    return response.data;
  }

  async toggleRead(id: number): Promise<Link> {
    const response = await this.post<Link>(`/link/${id}/read`);
    return response.data;
  }

  async advancedSearch(body: {
    conditions: Array<{ field: string; operator: string; value: string }>;
    operator: 'AND' | 'OR';
    collection_id?: number;
    tag_ids?: number[];
    page?: number;
    page_size?: number;
  }): Promise<any> {
    try {
      const response = await this.post('/link/advanced-search', body);
      return response.data;
    } catch (error) {
      console.error('Error in advancedSearch:', error);
      throw error;
    }
  }
}

export const linkService = new LinkService(); 